import { RenderPaymentStatus, renderDatetime } from '@/components/features/shared/utils'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Collector, getCollectorFullName } from '@/domain/collectors/collector'
import { InvoiceData } from '@/domain/invoices/invoice'
import { PaymentStatus, PaymentTransaction, formatPaymentMethod } from '@/domain/payments/payments'
import { useCollector, useCollectors } from '@/hooks/api/useCollectors'
import { useUpdatePaymentCollector } from '@/hooks/api/usePayments'
import { useUser } from '@/hooks/api/useUsers'
import { DollarSign, User as UserIcon } from 'lucide-react'
import { useState } from 'react'
import { DownloadButton } from '../sub-components/DownloadButton'

interface PaymentDetailModalProps {
  isOpen: boolean
  onClose: () => void
  payment: PaymentTransaction
  invoices?: InvoiceData[]
  onOpenDocument: ({ documentId, fileName }: { documentId: string, fileName: string }) => void
}

const NONE = '__none__'

export const PaymentDetailModal = ({
  isOpen,
  onClose,
  payment,
  invoices = [],
  onOpenDocument,
}: PaymentDetailModalProps) => {
  const { data: collector } = useCollector(payment.collectorId || '')
  const { data: user } = useUser(payment.createdBy)
  const updatePaymentCollector = useUpdatePaymentCollector()
  const { data: collectors = [] } = useCollectors()

  const [selectedCollectorId, setSelectedCollectorId] = useState(payment.collectorId ?? '')
  const isPending = payment.status === PaymentStatus.PENDING

  const handleCollectorChange = (value: string) => {
    const newCollectorId = value === NONE ? null : value
    setSelectedCollectorId(newCollectorId ?? '')
    updatePaymentCollector.mutate({ paymentId: payment!.id, collectorId: newCollectorId })
  }

  const formatMethodDetails = (details: any): string => {
    if (!details) return '-'
    if (typeof details === 'string') return details
    if (typeof details === 'object') {
      const parts: string[] = []
      if (details.obs) parts.push("Observaciones: " + details.obs)
      if (details.couponCode) parts.push("Cupón de pago: " + details.couponCode)
      if (details.legacy_id) parts.push("Letra-Sucursal-Numero-Renglon: " + details.legacy_id)
      return parts.join('\n')
    }
    return String(details)
  }

  const renderField = (label: string, value: React.ReactNode) => (
    <div className="space-y-1">
      <p className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider">{label}</p>
      <div className="text-sm font-medium">{value}</div>
    </div>
  )

  const paidLabel = payment.status === PaymentStatus.COMPLETED ? ' abonado' : ''

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Detalle del Pago</DialogTitle>
          <DialogDescription>
            Información completa de la transacción
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Amount Section */}
          <div className="bg-muted/30 rounded-xl p-4 border border-border/50">
            <div className="grid grid-cols-3 gap-4">
              <div>{renderField('Importe' + paidLabel, (
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                  <span className="font-mono text-lg font-bold">{payment.amount.toLocaleString('es-AR')}</span>
                </div>
              ))}</div>
              <div className="border-l border-border/50 pl-4">{renderField('Interés Tarjeta', (
                <div className="flex items-center gap-2">
                  <DollarSign className={`h-4 w-4 ${payment.interestSum > 0 ? 'text-warning' : 'text-muted-foreground'}`} />
                  <span className={`font-mono text-lg font-bold ${payment.interestSum > 0 ? 'text-warning' : 'text-muted-foreground'}`}>
                    {payment.interestSum.toLocaleString('es-AR')}
                  </span>
                </div>
              ))}</div>
              <div className="border-l border-border/50 pl-4">{renderField('Estado', (
                <RenderPaymentStatus paymentStatus={payment.status} collectorCode={collector?.code} />
              ))}</div>
            </div>
          </div>


          <div className="grid grid-cols-3 gap-4">
            {renderField('Fecha de generación', payment.generatedDate ? renderDatetime(payment.generatedDate) : '-')}
            {renderField('Fecha de pago', payment.status === PaymentStatus.COMPLETED && payment.paymentDate ? renderDatetime(payment.paymentDate) : '-')}
            {renderField('Usuario', user ? (
              <div className="flex items-center gap-2">
                <UserIcon className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">{user.fullName}</span>
              </div>
            ) : (
              <span className="text-muted-foreground">-</span>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-4">
            {renderField('Método de Pago', formatPaymentMethod(payment.paymentMethod))}
            {renderField('Cobrador', isPending ? (
              <Select
                value={selectedCollectorId}
                onValueChange={handleCollectorChange}
                disabled={updatePaymentCollector.isPending}
              >
                <SelectTrigger className="h-7 text-xs">
                  <SelectValue placeholder="Seleccionar cobrador" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Sin cobrador asignado</SelectItem>
                  {collectors.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.code} - {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : payment.collectorId && collector ? (
              <div className="flex items-center gap-2">
                <UserIcon className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">{getCollectorFullName(collector)}</span>
              </div>
            ) : (
              <span className="text-muted-foreground">Sin Cobrador</span>
            ))}
          </div>

          {/* Details */}
          {payment.paymentMethodDetails && (
            renderField('Observaciones', (
              <p className="text-sm bg-muted/30 p-2 rounded-lg" style={{ whiteSpace: 'pre-line' }}>{formatMethodDetails(payment.paymentMethodDetails)}</p>
            ))
          )}

          {/* Actions */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider mb-2">Facturación</p>
              <DownloadButton invoices={invoices} onOpenDocument={onOpenDocument} />
            </div>
            <div>
              {/* <p className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider mb-2">Movimiento de Cuenta</p>
              <Button
                variant="outline"
                size="sm"
                className="w-fit h-7" onClick={() => { }}>
                <span>Ver movimiento </span>
                <ArrowRight className="h-4 w-4" />
              </Button> */}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
